import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useAuth } from "../../contexts/AuthContext";
import { deferInvitation, listDeferredInvitations } from "../../lib/deferredInvitations";
import { myPendingInvitations } from "../../lib/listSharing";
import { List, ListMember } from "../../types";
import PendingInvitation from "./PendingInvitation";

interface InvitationToastsProps {
  listMembers: ListMember[];
  lists: List[];
}

/**
 * One corner prompt per unanswered Invitation that this person has not
 * already put off. Stays until they Accept, Decline, or Decide later —
 * Decide later points them at Settings → Shared with me and does not
 * show this prompt again.
 */
const InvitationToasts: React.FC<InvitationToastsProps> = ({ listMembers, lists }) => {
  const { user } = useAuth();
  const [deferredIds, setDeferredIds] = useState<Set<string>>(
    () => new Set(user ? listDeferredInvitations(user.id) : []),
  );

  useEffect(() => {
    setDeferredIds(new Set(user ? listDeferredInvitations(user.id) : []));
  }, [user]);

  const handleDeferred = useCallback(
    (listId: string) => {
      if (!user) return;
      deferInvitation(user.id, listId);
      setDeferredIds((prev) => new Set(prev).add(listId));
      toast("You can find this in Settings under Shared with me.", { duration: 6000 });
    },
    [user],
  );

  if (!user) return null;
  const invitations = myPendingInvitations(listMembers, user.id).filter(
    (invite) => !deferredIds.has(invite.list_id),
  );
  if (invitations.length === 0) return null;

  return (
    <div className="fixed top-5 right-5 z-[9999] w-[min(calc(100%-2.5rem),380px)] space-y-3">
      {invitations.map((invite) => (
        <PendingInvitation
          key={invite.list_id}
          invite={invite}
          lists={lists}
          onDefer={handleDeferred}
        />
      ))}
    </div>
  );
};

export default InvitationToasts;
